import { useEffect, useMemo, useState } from "react";

import { Dialog } from "primereact/dialog";
import { Button, ButtonProps } from "primereact/button";

import { useSandbox } from "../../contexts/SandboxHooks";

interface DialogAction {
  label: string;
  icon?: string;
  onClick: () => void;
  severity?: ButtonProps["severity"];
  iconPos?: ButtonProps["iconPos"];
  disabled?: boolean;
  className?: string;
}

const PostSubmissionModal = () => {
  const { testStatus, onLessonFinish, isSandboxLessonComplete } = useSandbox();

  const [modalVisible, setModalVisible] = useState(false);
  const [failureCode, setFailureCode] = useState<string | null>(null);

  useEffect(() => {
    if (!testStatus || testStatus.progress !== "complete") return;

    if (testStatus.status === "passed") {
      setFailureCode(null);
    } else {
      const code = testStatus.results?.find(r => r.failureCode)?.failureCode ?? null;
      setFailureCode(code);
    }
    setModalVisible(true);
  }, [testStatus]);

  const actions = useMemo<DialogAction[]>(
    () => [
      {
        label: "Finish lesson",
        icon: "check",
        onClick: () => {
          if (onLessonFinish) {
            onLessonFinish();
          } else {
            console.warn("onLessonFinish callback not provided");
          }
        },
      },
    ],
    [onLessonFinish]
  );

  const header = isSandboxLessonComplete ? "Submission Passed" : "Submission Failed";

  return (
    <Dialog
      visible={modalVisible}
      onHide={isSandboxLessonComplete ? () => {} : () => setModalVisible(false)}
      header={header}
      closable={!isSandboxLessonComplete}
      draggable={false}
      style={{ width: "32rem" }}
      modal
      data-testid="post-submission-modal"
      data-result={isSandboxLessonComplete ? "passed" : "failed"}
      footer={
        isSandboxLessonComplete ? (
          <div className="flex gap-2 justify-end">
            {actions.map((action, index) => (
              <Button
                key={index}
                severity={action.severity}
                icon={action.icon ? `pi pi-${action.icon}` : undefined}
                iconPos={action.iconPos ?? "left"}
                data-testid="post-submission-finish-button"
                onClick={action.onClick}
                disabled={action.disabled}
                className={action.className}
                label={action.label}
              />
            ))}
          </div>
        ) : (
          <div className="flex gap-2 justify-end">
            <Button
              label="Close"
              data-testid="post-submission-close-button"
              onClick={() => setModalVisible(false)}
            />
          </div>
        )
      }
    >
      {isSandboxLessonComplete ? (
        <p className="m-0">Congratulations, all tests have passed!</p>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="m-0">Your submission didn't pass all tests. Here's what went wrong:</p>
          <pre className="m-0 p-3 border-round surface-ground overflow-auto text-sm font-italic">
            {failureCode}
          </pre>
        </div>
      )}
    </Dialog>
  );
};

export default PostSubmissionModal;
